import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Container, Form, Button, Alert, Spinner, Card, Image, Row, Col, ProgressBar } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { 
  FaUpload, 
  FaCloudUploadAlt, 
  FaImages, 
  FaFileAlt, 
  FaTrash, 
  FaCheckCircle,
  FaExclamationTriangle,
  FaInfoCircle,
  FaImage,
  FaVideo,
  FaMusic,
  FaFilePdf,
  FaStar,
  FaBolt,
  FaLanguage,
  FaHome,
  FaPlus,
  FaTimes,
  FaPlayCircle,
  FaHeadphones,
  FaFileWord,
  FaFileExcel,
  FaFilePowerpoint,
  FaFile
} from 'react-icons/fa';

const UploadPage = () => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    content_type: 'news',
    category: 'general',
    author: 'KSA Files',
    tags: '',
    is_breaking: false,
    is_featured: false,
    language: 'en'
  });
  const [featuredMedia, setFeaturedMedia] = useState(null);
  const [additionalMedia, setAdditionalMedia] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [widgetLoaded, setWidgetLoaded] = useState(false);
  const [categories, setCategories] = useState([]);
  const [contentTypes, setContentTypes] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const navigate = useNavigate();

  // Load categories and content types from database
  useEffect(() => {
    const loadData = async () => {
      try {
        // Load categories
        const { data: categoriesData, error: catError } = await supabase
          .from('categories')
          .select('*')
          .order('sort_order');

        if (catError) {
          console.error('Error loading categories:', catError);
          // Use default categories
          setCategories([
            { id: 1, name: 'General', slug: 'general', icon: '📁' },
            { id: 2, name: 'Humanitarian', slug: 'humanitarian', icon: '❤️' },
            { id: 3, name: 'Media', slug: 'media', icon: '📰' },
            { id: 4, name: 'Islam', slug: 'religious', icon: '📚' },
            { id: 5, name: 'Cultural', slug: 'cultural', icon: '🎭' },
            { id: 6, name: 'Social', slug: 'social', icon: '👥' },
            { id: 7, name: 'Political', slug: 'political', icon: '🏛️' },
            { id: 8, name: 'Economic', slug: 'economic', icon: '💰' }
          ]);
        } else {
          const formattedCategories = categoriesData.map(cat => ({
            ...cat,
            name: cat.name || cat.name_ar,
            icon: '📁'
          }));
          setCategories(formattedCategories);
        }

        // Load content types
        const { data: contentTypesData, error: typeError } = await supabase
          .from('content_types')
          .select('*')
          .order('sort_order');

        if (typeError) {
          console.error('Error loading content types:', typeError);
          // Use default content types
          setContentTypes([
            { id: 1, name: 'News', slug: 'news', icon: '📰' },
            { id: 2, name: 'Video', slug: 'video', icon: '🎬' },
            { id: 3, name: 'Image', slug: 'image', icon: '🖼️' },
            { id: 4, name: 'Document', slug: 'document', icon: '📄' },
            { id: 5, name: 'Audio', slug: 'audio', icon: '🎵' },
            { id: 6, name: 'Report', slug: 'report', icon: '📊' },
            { id: 7, name: 'Statement', slug: 'statement', icon: '📢' },
            { id: 8, name: 'Article', slug: 'article', icon: '📝' }
          ]);
        } else {
          const formattedTypes = contentTypesData.map(type => ({
            ...type,
            name: type.name || type.name_ar,
            icon: type.icon || '📄'
          }));
          setContentTypes(formattedTypes);
        }
      } catch (err) {
        console.error('Error loading data:', err);
        setError('Failed to load data');
      }
    };

    loadData();
  }, []);

  // Load Cloudinary widget
  useEffect(() => {
    const scriptId = 'cloudinary-widget-script';
    
    // Remove existing script if any
    const existingScript = document.getElementById(scriptId);
    if (existingScript) {
      existingScript.remove();
    }

    const script = document.createElement('script');
    script.id = scriptId;
    script.src = 'https://upload-widget.cloudinary.com/global/all.js';
    script.async = true;
    
    script.onload = () => {
      console.log('Cloudinary widget loaded');
      setWidgetLoaded(true);
    };
    
    script.onerror = () => {
      console.error('Failed to load Cloudinary widget');
      setError('Failed to load upload widget');
    };
    
    document.body.appendChild(script);

    return () => {
      const script = document.getElementById(scriptId);
      if (script) {
        script.remove();
      }
    };
  }, []);

  const openCloudinaryWidget = (isFeatured = false, isDocument = false) => {
    if (!widgetLoaded) {
      setError('Upload widget is still loading. Please try again.');
      return;
    }

    if (!window.cloudinary || !window.cloudinary.createUploadWidget) {
      setError('Upload widget is not available. Please refresh the page.');
      return;
    }

    try {
      const cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || 'djcq8y6ml';
      const uploadPreset = isDocument 
        ? (process.env.REACT_APP_CLOUDINARY_DOC_UPLOAD_PRESET || 'ksa-documents')
        : (process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || 'irda-media');

      const widgetOptions = {
        cloudName: cloudName,
        uploadPreset: uploadPreset,
        sources: ['local', 'url'],
        multiple: !isFeatured,
        maxFiles: isFeatured ? 1 : 10,
        resourceType: isDocument ? 'raw' : 'auto',
        maxFileSize: 100 * 1024 * 1024, // 100MB limit
        showAdvancedOptions: false,
        cropping: false,
        styles: {
          palette: {
            window: '#FFFFFF',
            windowBorder: '#90A0B3',
            tabIcon: '#255853',
            menuIcons: '#5A616A',
            textDark: '#000000',
            textLight: '#FFFFFF',
            link: '#255853',
            action: '#255853',
            inactiveTabIcon: '#0E2F5A',
            error: '#F44235',
            inProgress: '#255853',
            complete: '#20B832',
            sourceBg: '#E4EBF1'
          }
        },
        clientAllowedFormats: isDocument 
          ? ['pdf', 'doc', 'docx', 'txt', 'xlsx', 'pptx']
          : ['jpg', 'jpeg', 'png', 'gif', 'webp', 'mp4', 'mov', 'avi', 'mp3', 'wav']
      };

      const uploadWidget = window.cloudinary.createUploadWidget(
        widgetOptions,
        (error, result) => {
          if (error) {
            console.error('Upload error:', error);
            setError('Upload error');
            return;
          }

          if (result && result.event === 'success') {
            const mediaData = {
              url: result.info.secure_url,
              public_id: result.info.public_id,
              format: result.info.format,
              size: result.info.bytes,
              resource_type: result.info.resource_type,
              original_filename: result.info.original_filename,
              width: result.info.width,
              height: result.info.height,
              duration: result.info.duration
            };

            if (isDocument) {
              setDocuments(prev => [...prev, mediaData]);
            } else if (isFeatured) {
              setFeaturedMedia(mediaData);
            } else {
              setAdditionalMedia(prev => [...prev, mediaData]);
            }
          }
        }
      );

      uploadWidget.open();
    } catch (err) {
      console.error('Widget error:', err);
      setError('Error opening upload widget');
    }
  };

  const removeMedia = (index, isFeatured = false, isDocument = false) => {
    if (isDocument) {
      setDocuments(prev => prev.filter((_, i) => i !== index));
    } else if (isFeatured) {
      setFeaturedMedia(null);
    } else {
      setAdditionalMedia(prev => prev.filter((_, i) => i !== index));
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setUploadProgress(0);
  
    // Validation (featured media removed from requirements)
    if (!formData.title.trim() || !formData.description.trim() || 
        !formData.content_type || !formData.category || !formData.author.trim()) {
      setError('Please fill in all required fields');
      setLoading(false);
      return;
    }
  
    try {
      setUploadProgress(20);
      const token = uuidv4();
      const now = new Date().toISOString();
  
      // Prepare tags array
      const tagsArray = formData.tags
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0);
  
      setUploadProgress(40);
  
      // Prepare content data with optional featured media
      const contentData = {
        title: formData.title,
        title_ar: formData.title,
        description: formData.description,
        description_ar: formData.description,
        content_type: formData.content_type,
        category: formData.category,
        author: formData.author,
        author_ar: formData.author,
        // Optional fields:
        featured_media: featuredMedia ? featuredMedia.url : null,
        featured_media_data: featuredMedia || null,
        additional_media: additionalMedia,
        documents: documents,
        tags: tagsArray,
        tags_ar: tagsArray,
        status: 'published',
        is_featured: formData.is_featured,
        is_breaking: formData.is_breaking,
        language: formData.language,
        token: token,
        views: 0,
        likes: 0,
        shares: 0,
        comments_count: 0,
        published_at: now,
        created_at: now,
        updated_at: now
      };
  
      setUploadProgress(60);
  
      // Insert into content table
      const { data, error: supabaseError } = await supabase
        .from('content')
        .insert([contentData])
        .select();
  
      if (supabaseError) {
        console.error('Supabase error:', supabaseError);
        throw new Error(supabaseError.message || 'Failed to save content');
      }
  
      setUploadProgress(80);
  
      // Also insert into content_tokens table
      if (data && data[0]) {
        const { error: tokenError } = await supabase
          .from('content_tokens')
          .insert([{
            token: token,
            content_id: data[0].id,
            title: formData.title,
            title_ar: formData.title,
            status: 'active',
            type: formData.content_type,
            created_at: now,
            updated_at: now
          }]);
  
        if (tokenError) {
          console.warn('Could not save token:', tokenError);
        }
      }
  
      setUploadProgress(100);
      setSuccess(true);
      
      // Reset form after 3 seconds
      setTimeout(() => {
        setFormData({
          title: '',
          description: '',
          content_type: 'news',
          category: 'general',
          author: 'KSA Files',
          tags: '',
          is_breaking: false,
          is_featured: false,
          language: 'en'
        });
        setFeaturedMedia(null);
        setAdditionalMedia([]);
        setDocuments([]);
        setSuccess(false);
        setUploadProgress(0);
      }, 3000);
  
    } catch (err) {
      console.error('Submit error:', err);
      setError(`Error saving content: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      content_type: 'news',
      category: 'general',
      author: 'KSA Files',
      tags: '',
      is_breaking: false,
      is_featured: false,
      language: 'en'
    });
    setFeaturedMedia(null);
    setAdditionalMedia([]);
    setDocuments([]);
    setSuccess(false);
    setError(null);
    setUploadProgress(0);
  };

  const getFileIcon = (format, resourceType) => {
    if (resourceType === 'image') return <FaImage className="text-primary" />;
    if (resourceType === 'video') return <FaVideo className="text-danger" />;
    if (resourceType === 'audio') return <FaMusic className="text-success" />;
    
    switch(format) {
      case 'pdf': return <FaFilePdf className="text-danger" />;
      case 'doc': case 'docx': return <FaFileWord className="text-primary" />;
      case 'xlsx': return <FaFileExcel className="text-success" />;
      case 'pptx': return <FaFilePowerpoint className="text-warning" />;
      default: return <FaFile className="text-secondary" />;
    }
  };

  // Format bytes to human readable
  const formatBytes = (bytes, decimals = 2) => {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  };

  return (
    <div className="upload-page-container">
      <style>{`
        .upload-page-container {
          min-height: 100vh;
          background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
          padding: 20px;
          font-family: 'ITF Qomra Arabic', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          direction: ltr;
        }

        @media (max-width: 768px) {
          .upload-page-container {
            padding: 10px;
          }
        }

        .upload-header {
          text-align: center;
          padding: 40px 20px;
          background: linear-gradient(135deg, #255853 0%, #357E77 100%);
          color: white;
          border-radius: 15px;
          margin-bottom: 30px;
          box-shadow: 0 10px 30px rgba(37, 88, 83, 0.2);
        }

        @media (max-width: 768px) {
          .upload-header {
            padding: 25px 15px;
            margin-bottom: 20px;
          }
        }

        .upload-card {
          border: none;
          border-radius: 15px;
          box-shadow: 0 8px 30px rgba(0,0,0,0.1);
          margin-bottom: 30px;
          overflow: hidden;
        }

        @media (max-width: 768px) {
          .upload-card {
            border-radius: 12px;
            margin-bottom: 20px;
          }
        }

        .form-label {
          font-weight: 600;
          color: #255853;
          margin-bottom: 8px;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .form-control-custom {
          border: 2px solid #e0e6ed;
          border-radius: 10px;
          padding: 12px 15px;
          font-size: 16px;
          transition: all 0.3s;
          font-family: 'ITF Qomra Arabic', sans-serif;
        }

        .form-control-custom:focus {
          border-color: #255853;
          box-shadow: 0 0 0 3px rgba(37, 88, 83, 0.1);
          outline: none;
        }

        @media (max-width: 768px) {
          .form-control-custom {
            padding: 10px 12px;
            font-size: 14px;
          }
        }

        .upload-button {
          background: linear-gradient(135deg, #255853 0%, #357E77 100%);
          border: none;
          padding: 12px 25px;
          border-radius: 25px;
          color: white;
          font-weight: 600;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          transition: all 0.3s;
          font-family: 'ITF Qomra Arabic', sans-serif;
          font-size: 16px;
          min-width: 180px;
        }

        @media (max-width: 768px) {
          .upload-button {
            padding: 10px 20px;
            font-size: 14px;
            min-width: 150px;
            width: 100%;
          }
        }

        .upload-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(37, 88, 83, 0.3);
        }

        .upload-button:disabled {
          background: #6c757d;
          cursor: not-allowed;
          transform: none;
        }

        .media-preview-container {
          background: #f8f9fa;
          border: 2px dashed #dee2e6;
          border-radius: 12px;
          padding: 20px;
          margin-top: 15px;
        }

        @media (max-width: 768px) {
          .media-preview-container {
            padding: 15px;
          }
        }

        .submit-button {
          background: linear-gradient(135deg, #255853 0%, #357E77 100%);
          border: none;
          padding: 15px 30px;
          font-size: 18px;
          font-weight: 700;
          border-radius: 25px;
          width: 100%;
          margin-top: 30px;
          transition: all 0.3s;
          color: white;
          font-family: 'ITF Qomra Arabic', sans-serif;
        }

        @media (max-width: 768px) {
          .submit-button {
            padding: 12px 20px;
            font-size: 16px;
            margin-top: 20px;
          }
        }

        .submit-button:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(37, 88, 83, 0.3);
        }

        .submit-button:disabled {
          background: #6c757d;
          cursor: not-allowed;
        }

        .success-container {
          text-align: center;
          padding: 40px 20px;
        }

        @media (max-width: 768px) {
          .success-container {
            padding: 30px 15px;
          }
        }

        .media-item {
          background: white;
          border: 1px solid #dee2e6;
          border-radius: 10px;
          padding: 15px;
          transition: all 0.3s;
        }

        .media-item:hover {
          box-shadow: 0 4px 15px rgba(0,0,0,0.1);
          transform: translateY(-2px);
        }

        .checkbox-container {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px;
          background: #f8f9fa;
          border-radius: 8px;
          margin-bottom: 10px;
        }

        .progress-container {
          margin: 20px 0;
        }

        .file-info {
          font-size: 14px;
          color: #6c757d;
          margin-top: 5px;
        }

        .section-header {
          color: #255853;
          margin-bottom: 20px;
          padding-bottom: 10px;
          border-bottom: 2px solid #e0e6ed;
          display: flex;
          align-items: center;
          gap: 10px;
          font-weight: 700;
        }

        @media (max-width: 768px) {
          .section-header {
            font-size: 18px;
            margin-bottom: 15px;
          }
        }

        .remove-btn {
          background: #dc3545;
          border: none;
          color: white;
          width: 30px;
          height: 30px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s;
        }

        .remove-btn:hover {
          background: #c82333;
          transform: scale(1.1);
        }

        .action-buttons {
          display: flex;
          gap: 10px;
          justify-content: center;
          margin-top: 20px;
        }

        @media (max-width: 768px) {
          .action-buttons {
            flex-direction: column;
          }
        }

        .mobile-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 10px;
        }

        @media (max-width: 480px) {
          .mobile-grid {
            grid-template-columns: 1fr;
          }
        }

        .empty-state {
          text-align: center;
          padding: 30px;
          color: #6c757d;
          background: #f8f9fa;
          border-radius: 10px;
          border: 2px dashed #dee2e6;
        }

        .error-alert {
          background: #f8d7da;
          border: 1px solid #f5c6cb;
          color: #721c24;
          padding: 15px;
          border-radius: 8px;
          margin-bottom: 20px;
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .success-alert {
          background: #d4edda;
          border: 1px solid #c3e6cb;
          color: #155724;
          padding: 15px;
          border-radius: 8px;
          margin-bottom: 20px;
          display: flex;
          align-items: center;
          gap: 10px;
        }
      `}</style>

      <Container>
        {/* Header */}
        <div className="upload-header">
          <h1 style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '10px' }}>
            <FaUpload style={{ marginRight: '10px' }} />
            Upload Content
          </h1>
          <p style={{ fontSize: '1.2rem', opacity: 0.9, maxWidth: '800px', margin: '0 auto' }}>
            Upload any type of content to the platform
          </p>
        </div>

        {/* Main Content */}
        <Card className="upload-card">
          <Card.Body style={{ padding: '30px' }}>
            {success ? (
              <div className="success-container">
                <FaCheckCircle style={{ fontSize: '4rem', color: '#28a745', marginBottom: '20px' }} />
                <h3 style={{ color: '#155724', marginBottom: '15px' }}>
                  Content published successfully!
                </h3>
                <p style={{ marginBottom: '30px', fontSize: '1.1rem' }}>
                  Your content has been successfully published on the platform.
                </p>
                {uploadProgress > 0 && uploadProgress < 100 && (
                  <ProgressBar now={uploadProgress} label={`${uploadProgress}%`} className="mb-4" />
                )}
                <div className="action-buttons">
                  <Button 
                    variant="outline-primary" 
                    onClick={() => navigate('/')}
                    style={{ 
                      borderRadius: '25px',
                      padding: '10px 25px',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <FaHome />
                    Back to Home
                  </Button>
                  <Button 
                    variant="primary" 
                    onClick={resetForm}
                    style={{ 
                      borderRadius: '25px',
                      padding: '10px 25px',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <FaPlus />
                    Upload New Content
                  </Button>
                </div>
              </div>
            ) : (
              <Form onSubmit={handleSubmit}>
                {error && (
                  <div className="error-alert">
                    <FaExclamationTriangle />
                    {error}
                  </div>
                )}

                {/* Upload Progress */}
                {uploadProgress > 0 && uploadProgress < 100 && (
                  <div className="progress-container">
                    <div className="d-flex justify-content-between mb-2">
                      <span>Publishing...</span>
                      <span>{uploadProgress}%</span>
                    </div>
                    <ProgressBar now={uploadProgress} />
                  </div>
                )}

                {/* Basic Information Section */}
                <div className="mb-5">
                  <h4 className="section-header">
                    <FaInfoCircle />
                    Basic Information
                  </h4>

                  {/* Title */}
                  <Form.Group className="mb-4">
                    <Form.Label className="form-label">
                      <FaFileAlt />
                      Title *
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter content title"
                      className="form-control-custom"
                    />
                  </Form.Group>

                  {/* Description */}
                  <Form.Group className="mb-4">
                    <Form.Label className="form-label">
                      <FaFileAlt />
                      Description *
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      required
                      placeholder="Describe your content"
                      className="form-control-custom"
                    />
                  </Form.Group>

                  <Row>
                    {/* Content Type */}
                    <Col md={6}>
                      <Form.Group className="mb-4">
                        <Form.Label className="form-label">
                          <FaFileAlt />
                          Content Type *
                        </Form.Label>
                        <Form.Select
                          name="content_type"
                          value={formData.content_type}
                          onChange={handleInputChange}
                          required
                          className="form-control-custom"
                        >
                          {contentTypes.map((type) => (
                            <option key={type.slug || type.value} value={type.slug || type.value}>
                              {type.icon} {type.name}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>

                    {/* Category */}
                    <Col md={6}>
                      <Form.Group className="mb-4">
                        <Form.Label className="form-label">
                          <FaFileAlt />
                          Category *
                        </Form.Label>
                        <Form.Select
                          name="category"
                          value={formData.category}
                          onChange={handleInputChange}
                          required
                          className="form-control-custom"
                        >
                          {categories.map((cat) => (
                            <option key={cat.slug || cat.value} value={cat.slug || cat.value}>
                              {cat.icon} {cat.name}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    {/* Author */}
                    <Col md={6}>
                      <Form.Group className="mb-4">
                        <Form.Label className="form-label">
                          <FaFileAlt />
                          Author *
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="author"
                          value={formData.author}
                          onChange={handleInputChange}
                          required
                          placeholder="Enter author name"
                          className="form-control-custom"
                        />
                      </Form.Group>
                    </Col>

                    {/* Language */}
                    <Col md={6}>
                      <Form.Group className="mb-4">
                        <Form.Label className="form-label">
                          <FaLanguage />
                          Content Language
                        </Form.Label>
                        <Form.Select
                          name="language"
                          value={formData.language}
                          onChange={handleInputChange}
                          className="form-control-custom"
                        >
                          <option value="en">English</option>
                          <option value="ar">Arabic</option>
                          <option value="both">Both</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>

                  {/* Tags */}
                  <Form.Group className="mb-4">
                    <Form.Label className="form-label">
                      <FaFileAlt />
                      Tags
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="tags"
                      value={formData.tags}
                      onChange={handleInputChange}
                      placeholder="Comma-separated tags"
                      className="form-control-custom"
                    />
                  </Form.Group>
                </div>

                {/* Featured Media Section */}
                <div className="mb-5">
                  <h4 className="section-header">
                    <FaStar />
                    Featured Media
                  </h4>
                  
                  <button 
                    type="button"
                    onClick={() => openCloudinaryWidget(true, false)}
                    className="upload-button"
                    disabled={uploading}
                  >
                    <FaCloudUploadAlt />
                    {uploading ? 'Uploading...' : 'Upload Featured Media'}
                  </button>
                  
                  <small className="d-block text-muted mt-2">
                    <FaInfoCircle style={{ marginRight: '5px' }} />
                    Max file size: 100MB | Supported formats: JPG, PNG, GIF, MP4, MP3
                  </small>

                  {featuredMedia ? (
                    <div className="media-preview-container">
                      <div className="d-flex flex-column flex-md-row align-items-center justify-content-between gap-3">
                        <div className="d-flex align-items-center gap-3">
                          {featuredMedia.resource_type === 'image' ? (
                            <Image 
                              src={featuredMedia.url} 
                              rounded
                              style={{ 
                                width: '80px', 
                                height: '80px', 
                                objectFit: 'cover'
                              }}
                            />
                          ) : (
                            <div style={{
                              width: '80px',
                              height: '80px',
                              backgroundColor: '#e9ecef',
                              borderRadius: '8px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              {featuredMedia.resource_type === 'video' ? 
                                <FaPlayCircle size={30} color="#255853" /> : 
                                <FaHeadphones size={30} color="#255853" />
                              }
                            </div>
                          )}
                          <div>
                            <p style={{ margin: 0, fontWeight: '600' }}>
                              {featuredMedia.original_filename || 'Featured Media'}
                            </p>
                            <p className="file-info">
                              {featuredMedia.format?.toUpperCase()} • {formatBytes(featuredMedia.size)}
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          className="remove-btn"
                          onClick={() => removeMedia(0, true, false)}
                        >
                          <FaTimes />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="empty-state mt-3">
                      <FaImage size={40} className="mb-3" color="#6c757d" />
                      <p>No featured media selected</p>
                    </div>
                  )}
                </div>

                {/* Additional Media Section */}
                <div className="mb-5">
                  <h4 className="section-header">
                    <FaImages />
                    Additional Media
                  </h4>
                  
                  <button 
                    type="button"
                    onClick={() => openCloudinaryWidget(false, false)}
                    className="upload-button"
                  >
                    <FaCloudUploadAlt />
                    Upload Media
                  </button>

                  {additionalMedia.length > 0 ? (
                    <div className="media-preview-container mt-3">
                      <div className="mobile-grid">
                        {additionalMedia.map((media, index) => (
                          <div key={index} className="media-item">
                            <div className="position-relative">
                              <div className="d-flex align-items-center gap-3">
                                <div style={{
                                  width: '60px',
                                  height: '60px',
                                  borderRadius: '8px',
                                  backgroundColor: '#e9ecef',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}>
                                  {getFileIcon(media.format, media.resource_type)}
                                </div>
                                <div style={{ flex: 1 }}>
                                  <p style={{ margin: 0, fontWeight: '600', fontSize: '14px' }}>
                                    {media.original_filename || `Media ${index + 1}`}
                                  </p>
                                  <p className="file-info">
                                    {media.format?.toUpperCase()} • {formatBytes(media.size)}
                                  </p>
                                </div>
                              </div>
                              <button
                                type="button"
                                className="remove-btn position-absolute"
                                style={{ top: '5px', right: '5px' }}
                                onClick={() => removeMedia(index, false, false)}
                              >
                                <FaTimes size={12} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="empty-state mt-3">
                      <FaImages size={40} className="mb-3" color="#6c757d" />
                      <p>No additional media uploaded</p>
                    </div>
                  )}
                </div>

                {/* Documents Section */}
                <div className="mb-5">
                  <h4 className="section-header">
                    <FaFileAlt />
                    Documents
                  </h4>
                  
                  <button 
                    type="button"
                    onClick={() => openCloudinaryWidget(false, true)}
                    className="upload-button"
                  >
                    <FaCloudUploadAlt />
                    Upload Documents
                  </button>

                  {documents.length > 0 ? (
                    <div className="media-preview-container mt-3">
                      {documents.map((doc, index) => (
                        <div key={index} className="media-item mb-3">
                          <div className="d-flex align-items-center justify-content-between">
                            <div className="d-flex align-items-center gap-3">
                              <div style={{ fontSize: '24px' }}>
                                {getFileIcon(doc.format)}
                              </div>
                              <div>
                                <p style={{ margin: 0, fontWeight: '600' }}>
                                  {doc.original_filename || `Document ${index + 1}`}
                                </p>
                                <p className="file-info">
                                  {doc.format?.toUpperCase()} • {formatBytes(doc.size)}
                                </p>
                              </div>
                            </div>
                            <button
                              type="button"
                              className="remove-btn"
                              onClick={() => removeMedia(index, false, true)}
                            >
                              <FaTrash size={14} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="empty-state mt-3">
                      <FaFileAlt size={40} className="mb-3" color="#6c757d" />
                      <p>No documents uploaded</p>
                    </div>
                  )}
                </div>

                {/* Submit Button */}
                <Button 
                  type="submit" 
                  disabled={loading}
                  className="submit-button"
                >
                  {loading ? (
                    <>
                      <Spinner as="span" animation="border" size="sm" className="me-2" />
                      Publishing...
                    </>
                  ) : (
                    <>
                      <FaUpload className="me-2" />
                      Publish Content
                    </>
                  )}
                </Button>
              </Form>
            )}
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default UploadPage;